import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import type { Theme } from "@mui/material/styles";
import { FeatureIcon, type FeatureIconName } from "./icons/FeatureIcons";

/**
 * The landing-page card. One anatomy (icon, title, body) for every card, in
 * two shapes:
 *
 *   tile — vertical, 40 px icon tile top-left. For the two 4-card groups.
 *   band — horizontal, icon column left, filled surface, no border. For the
 *          provider list, which reads as a scannable list not a grid.
 *
 * The GROUP shows only through the icon treatment, never through a different
 * card shape:
 *   what     — ink icon on a soft amber tile
 *   ai       — amber icon on an ink tile (🎨 the second amber element on the
 *              landing page after the CTA; the provider list gets none)
 *   provider — plain icon, no tile
 */
export type FeatureGroup = "what" | "ai" | "provider";

interface Props {
  icon: FeatureIconName;
  title: string;
  body: string;
  group: FeatureGroup;
  variant?: "tile" | "band";
}

export function FeatureCard({ icon, title, body, group, variant = "tile" }: Props) {
  const iconColor = group === "ai" ? "secondary.main" : "text.primary";
  // AI tile: ink in light mode, the page background (darker than the card) in
  // dark mode — amber on either clears 6:1, amber on paper would not.
  const tileBg = (t: Theme) =>
    group === "ai"
      ? t.palette.mode === "dark"
        ? t.palette.background.default
        : t.palette.text.primary
      : t.palette.brand.accentSoft;

  if (variant === "band") {
    return (
      <Box
        sx={{
          height: "100%",
          bgcolor: "brand.surface2",
          borderRadius: "14px",
          px: 2.5,
          py: 2.25,
          display: "flex",
          gap: 2,
          alignItems: "flex-start",
        }}
      >
        <Box sx={{ flexShrink: 0, width: 32, display: "flex", justifyContent: "center", pt: 0.25, color: iconColor }}>
          <FeatureIcon name={icon} size={28} strokeWidth={1.75} />
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {body}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: "100%",
        bgcolor: "background.paper",
        border: 1,
        borderColor: "divider",
        borderRadius: "12px",
        p: 2.5,
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: "10px",
          bgcolor: tileBg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: iconColor,
        }}
      >
        <FeatureIcon name={icon} size={22} />
      </Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {body}
      </Typography>
    </Box>
  );
}
